<script setup lang="ts">
import querystring from "~/helpers/http/querystring";
import { useIntersectionObserver } from "@vueuse/core";
import type {
	ArticleResponse,
	ArticleCategory,
	ContentGridDynamic,
	ContentGridDynamicFilters,
	ContentGridDynamicCard,
	CardSize,
	Division,
} from "./ContentGridDynamicTypes";

const props = withDefaults(defineProps<ContentGridDynamic>(), {
	initialCategory: "",
	infinityScroll: false,
	showFilters: false,
	loadMoreButton: false,
	categoryPage: undefined,
	pinned: () => [],
	initialSubcategories: () => [],
});

const loading = ref(true);
const infiniteScrollIntersect = ref(null);

const showHeader = computed(() => {
	return props.categoryPage || useRoute().query.category || useRoute().query.subcategories;
});

const heading = computed(() => {
	if (useRoute().query.subcategories) return filters.subcategories[0];

	if (useRoute().query.category) return filters.selectedCategory;

	if (props.categoryPage) {
		if (props.initialSubcategories?.length > 0) return props.initialSubcategories[0];
		else if (props.initialCategory) return props.initialCategory;

		if (props.division) {
			return divisionMap[props.division];
		}
	}
	return "";
});

const dynamicCategoryPageUrl = computed(() => {
	if (props.categoryPage) {
		return `${props.categoryPage}?${querystring.toString({
			category: filters.selectedCategory,
			subcategories: filters.subcategories,
		})}`;
	} else {
		return "";
	}
});

const divisionMap = {
	mobile: "Mobil",
	fixed: "Internett og TV",
	business: "Bedrift",
	press: "Telenor",
};

const { fetchArticles, fetchPinnedArticles, cardSize, filters, canLoadMore, articleResults, pendingBatchSize } =
	useArticleService(props.division);

onMounted(async () => {
	if (props.pinned.length) {
		await fetchPinnedArticles();
	}
	if (props.division) {
		await fetchArticles();
	} else {
		pendingBatchSize.value = 0;
	}
});

async function handleCategoryChanged(newCategory) {
	if (!newCategory) {
		filters.selectedCategory = props.initialCategory;
	} else {
		filters.selectedCategory = newCategory;
	}
	filters.selectedSubcategory = filters.subcategories[0];
	articleResults.articles = [];
	await fetchArticles();
}

async function handleSubcategoryChanged(newSubcategory) {
	if (!newSubcategory) {
		filters.subcategories = props.initialSubcategories;
	} else {
		filters.subcategories = Array.from(new Set([...props.initialSubcategories, newSubcategory])).filter(Boolean);
	}
	articleResults.articles = [];
	await fetchArticles();
}

async function handleTermChanged(newTerm) {
	filters.term = newTerm;
	articleResults.articles = [];
	await fetchArticles({ termChanged: true });
}

useIntersectionObserver(
	infiniteScrollIntersect,
	async ([entry], _observerElement) => {
		if (entry?.isIntersecting && canLoadMore.value) {
			await fetchArticles();
		}
	},
	{ rootMargin: "50%" },
);

function useArticleService(division: Division) {
	const filters = reactive<ContentGridDynamicFilters>({
		selectedCategory: props.initialCategory,
		selectedSubcategory: props.initialSubcategories.length === 1 ? props.initialSubcategories[0] : "",
		subcategories: props.initialSubcategories,
		categoryOptions: [] as ArticleCategory[],
		subcategoryOptions: [] as ArticleCategory[],
		term: "",
	});

	const articleResults = reactive({
		total: 0,
		articles: [] as ContentGridDynamicCard[],
		error: null,
		pinnedArticles: [] as ContentGridDynamicCard[],
		pinnedError: null,
	});

	const pendingBatchSize = ref(props.maxItems);
	const canLoadMore = computed(() => articleResults.articles.length < articleResults.total);

	const cardSize = computed<CardSize>(() => {
		const numberOfItems =
			articleResults.articles.length + articleResults.pinnedArticles.length === 0
				? props.maxItems
				: articleResults.articles.length + articleResults.pinnedArticles.length;

		if (numberOfItems === 1) return "xl";
		else if (numberOfItems === 2) return "lg";
		else if (numberOfItems === 3) return "md";
		else return "sm";
	});

	async function fetchPinnedArticles() {
		try {
			const pinned = await $fetch<ArticleResponse>("/api/articles/pinned/", {
				method: "POST",
				body: { urls: props.pinned },
				query: { from: 0, size: props.maxItems },
			});

			if (pinned.articles) {
				articleResults.pinnedArticles = pinned.articles.map(setArticle);
			}
		} catch (error) {
			articleResults.pinnedError = error;
		}
	}

	async function fetchArticles({ termChanged = false } = {}) {
		if (!division) return;
		try {
			loading.value = true;

			if (articleResults.articles.length === 0) {
				pendingBatchSize.value = props.maxItems - articleResults.pinnedArticles.length;
			} else {
				pendingBatchSize.value = props.maxItems;
			}

			const { articles, categories, subcategories, total } = await $fetch<ArticleResponse>(
				`/api/articles/${division}`,
				{
					query: {
						category: filters.selectedCategory || props.initialCategory,
						subcategories: filters.subcategories,
						from: articleResults.articles.length,
						size: pendingBatchSize.value,
						term: filters.term,
						excludeIds: articleResults.articles.map((article) => article.id).filter(Boolean),
						mode: filters.subcategoryOptions.length > props.initialSubcategories.length ? "AND" : "OR",
					},
				},
			);

			articleResults.articles = [...articleResults.articles, ...articles.map(setArticle)];
			articleResults.total = total;

			if (filters.subcategories.length === props.initialSubcategories.length || termChanged) {
				filters.subcategoryOptions = subcategories;

				if (props.initialSubcategories.length === 1) {
					filters.selectedSubcategory = filters.subcategoryOptions.find(
						(option) => option.key === props.initialSubcategories[0],
					)?.key;
				}
			}

			if (filters.categoryOptions.length < 1 || filters.selectedCategory === props.initialCategory) {
				filters.categoryOptions = categories;
			}
		} catch (error) {
			articleResults.error = error;
		} finally {
			pendingBatchSize.value = 0;
			loading.value = false;
		}
	}

	function setArticle(article): ContentGridDynamicCard {
		return {
			id: article.id || undefined,
			heading: article.heading || "",
			link: article.articleLink.replace("index.page", "") || "",
			image: {
				src: article.image.image_md.src || "",
				alt: article.image.alt || "",
			},
		};
	}

	return {
		fetchArticles,
		fetchPinnedArticles,
		articleResults,
		cardSize,
		filters,
		canLoadMore,
		pendingBatchSize,
	};
}
</script>
<template>
	<div>
		<div
			class="grid-columns-2 margin-bottom-xl"
			v-if="showHeader"
		>
			<TnHeading type="h2">{{ heading }}</TnHeading>
			<div
				class="text-right"
				v-if="categoryPage"
			>
				<TnLink
					:to="dynamicCategoryPageUrl"
					arrow-right
				>
					Mer om {{ heading }}
				</TnLink>
			</div>
		</div>
		<template v-if="showFilters">
			<ContentGridDynamicFilters
				v-bind="filters"
				:initial-category="initialCategory"
				@update:term="handleTermChanged"
				@categoryChanged="handleCategoryChanged"
				@subcategoryChanged="handleSubcategoryChanged"
			/>
		</template>
		<div :class="['content-grid', 'grid-row-gap-xl']">
			<template v-if="!loading && articleResults.articles.length === 0 && filters.term">
				<TnParagraph
					style="grid-column: 1 / -1"
					color="gray"
					responsive
					>Ingen treff for "{{ filters.term }}"...</TnParagraph
				>
			</template>
			<ContentGridDynamicCard
				v-for="article in [...articleResults.pinnedArticles, ...articleResults.articles]"
				:key="article.link"
				v-bind="article"
				:size="cardSize"
			/>
			<template v-if="loading">
				<ContentGridDynamicSkeletonCard
					v-for="item in pendingBatchSize"
					:size="cardSize"
					:key="item"
				/>
			</template>
		</div>
		<ClientOnly v-if="infinityScroll && canLoadMore">
			<div ref="infiniteScrollIntersect"></div>
		</ClientOnly>
		<TnButton
			v-if="loadMoreButton && canLoadMore"
			:loading="loading"
			:disabled="!canLoadMore"
			secondary
			@click="fetchArticles"
			class="margin-top-xl"
			>Last flere artikler</TnButton
		>
	</div>
</template>

<style scoped lang="scss">
@use "@/assets/typography/scss/mixins" as typoMixins;

.content-grid {
	width: 100%;
	display: grid;
	grid-template-columns: repeat(12, 1fr);
	padding-bottom: $spacing-l;
	gap: $spacing-l $spacing-l;

	@include breakpoint(mobile) {
		gap: $spacing-l $spacing-m;
		padding-bottom: $spacing-s;
	}
}
</style>
